import { Model } from 'core/model';
import { omit } from 'lodash';
import { observable } from 'mobx';
import { defaultSerializer } from 'app/util/nms/serializations';
import {
  DEFAULT_MONITORING_TEMPLATE_PRESET_ID,
  DefaultMonitoringTemplates
} from '@kentik/ui-shared/nms/deviceMonitoringTemplates';
import InterfaceGroupModel from '../interfaceGroup/InterfaceGroupModel';

const managedProfile = Object.values(DefaultMonitoringTemplates).find(
  ({ preset }) => preset === DEFAULT_MONITORING_TEMPLATE_PRESET_ID
);

export default class MonitoringTemplateModel extends Model {
  @observable.ref
  interfaceGroup = new InterfaceGroupModel();

  get urlRoot() {
    return '/api/ui/recon/monitoringTemplates';
  }

  get omitDuringSerialize() {
    return ['simple', 'advanced', 'device_count', 'user_full_name', '_interfaceGroup', 'measurement'];
  }

  get isPreset() {
    return this.get('preset') > 0;
  }

  duplicate = (options = {}) => {
    const { isNew = false, save = false, deserialize = true } = options;
    const hasInterfaceGroup = this.has('interface_group_id');
    const attributes = {
      ...omit(this.get(), ['id', 'interface_group_id']),
      ...(hasInterfaceGroup && { _interfaceGroup: 'dirty' }),
      name: isNew ? '' : `Copy of ${this.get('name')}`,
      description: '',
      preset: 0
    };
    const interfaceGroup = new InterfaceGroupModel(omit(this.interfaceGroup.toJS(), ['id']));

    if (!this.collection) {
      const dupe = new this.constructor(attributes);
      dupe.interfaceGroup = interfaceGroup;
      return dupe;
    }

    const dupe = this.collection.build(attributes, { deserialize });
    dupe.interfaceGroup = interfaceGroup;

    if (save) {
      dupe.save(attributes);
    }

    return dupe;
  };

  deserialize(data) {
    const normalizedData =
      data.preset === DEFAULT_MONITORING_TEMPLATE_PRESET_ID
        ? { ...data, name: managedProfile.name, description: managedProfile.description }
        : data;
    const { interface_group_id, settings } = normalizedData;
    const device_count = Number.parseInt(data.device_count) || 0;
    this.interfaceGroup.set('id', interface_group_id);
    const deserialized = {
      ...normalizedData,
      device_count,
      uiProperties: {
        polling: {
          snmp: {
            useDefaults: Boolean(!settings?.polling)
          }
        }
      }
    };
    return deserialized;
  }

  serialize(data) {
    const saveInterfaceGroup = !!data._interfaceGroup && (this.interfaceGroup.hasFilters || !this.interfaceGroup.isNew);
    const interfaceGroup = saveInterfaceGroup ? this.interfaceGroup.serialize(this.interfaceGroup.get()) : null;
    const template = { ...data, interfaceGroup };
    const { uiProperties } = data;

    // Omit polling settings if using NMS defaults
    if (uiProperties?.polling?.snmp?.useDefaults) {
      delete template.settings.polling;
    }

    // Remove uiProperties
    const serialized = defaultSerializer(template);

    return super.serialize(serialized);
  }
}
